/**
 * 通过ducc配置需要通过微前端、iframe和链接嵌入的页面。duccKey: YX_DUCC_ROUTES
 * 页面加载时从后端拉取menulist和ducc配置的routes信息一起存到store中，在文件src/components/loading-fullpage/index.tsx中
 * 路由：渲染整体路由和动态加载路由和手动触发路由更新这三个场景都会拿到ducc配置的routes将需要更新的路由重新生成一遍，确保正确插入。
 * 菜单：菜单渲染时会根据store中的duccroutes生成新的菜单。
 */
import Iframe from '@/components/iframe';
import Microapp from '@/components/micro-app';
import { getValueByEnv } from '@/env';

function transformData(data) {
  if (data == null || typeof data !== 'object') return data;

  return Object.keys(data).reduce((total, cur) => {
    const val = data[cur];
    total[cur] = isEnvData(val) ? getValueByEnv(val) : val;
    return total;
  }, {});

  function isEnvData(val) {
    return val && typeof val === 'object' && val.hasOwnProperty('envProd');
  }
}

const DUCC_INJECT_TYPES = {
  IFRAME: 1,
  MICROAPP: 2,
  JUMP_OUT: 3,
  TEST_DUCC: -1,
};
/**
 * title: xx
 * path: xx
 * menuId: xx
 * injectType: 1: iframe，2: microapp，3: jumpout
 * url: xxx injectType为1或者2的时候是嵌入链接，是3的时候是跳转链接
 *
 * // injectIn
 * parentKey: 'menuId | menuCode',
 * parentId: xxx
 * index: xxx
 */
// https://joyspace.jd.com/pages/5rADq9CGGQ91uBR8uZ5J

function pushDuccRoutesIn(fullRoutes = [], parentId, duccRoutes = []) {
  function duccRouteItemMapToRouteItem(duccRouteItem) {
    return {
      ...duccRouteItem,
      component() {
        if (DUCC_INJECT_TYPES.TEST_DUCC === duccRouteItem.injectType) return <div>这是duccroutes的测试页面</div>;
        if (DUCC_INJECT_TYPES.IFRAME === duccRouteItem.injectType)
          return <Iframe iframename={duccRouteItem.iframename} src={duccRouteItem.injectUrl} />;
        // if (DUCC_INJECT_TYPES.IFRAME === config.injectType) return <div>dddddd</div>
        if (DUCC_INJECT_TYPES.MICROAPP === duccRouteItem.injectType)
          return <Microapp basenameNoEndSlash data={transformData(duccRouteItem.dataToMicro)} url={duccRouteItem.injectUrl} name={duccRouteItem.microAppName || duccRouteItem.path.split('/').filter(item => item !== '*').join('_')} />;
        return null;
      },
    };
  }

  function filterDuccRoutesInParentId(parentId, item, fullRoutes) {
    // 如果是空则说明异步加载还没有完成，等加载完成了会重新进来计算
    // 如果对动态加载路由更新补充路由会导致页面404，因为
    // /a/b 根路由/a是动态路由，我们要为/a补充路由/c，如果不识别动态路由:
    // 补充ducc路由后{path: '/a', menus: [{path: '/c'}]}但是当前路由是/a/b就会跳转到404
    // 等动态路由补充后会是{path: '/a', menus: [{path: '/b'}, {path: '/c'}]}则不会有问题
    // 另外动态路由的添加在过滤的时候可能被漏掉导致页面404，所以在出现这个问题的时候需要
    // 配置一个和当前menuId一样的权限
    const parentIsAsyncRoute = !fullRoutes?.length;

    return (
      item.parentId === parentId &&
      item.injectType !== DUCC_INJECT_TYPES.JUMP_OUT &&
      (!parentIsAsyncRoute || item.forceInsert)
    );
  }

  return pushDuccConfigsIn(fullRoutes, parentId, duccRoutes, filterDuccRoutesInParentId, duccRouteItemMapToRouteItem);
}

function pushSideMenusIn(fullRoutes = [], parentId, duccRoutes = []) {
  function duccRouteItemMapToSideMenuItem(duccRouteItem) {
    const {injectType, sideMenuLink} = duccRouteItem
    if (injectType === DUCC_INJECT_TYPES.JUMP_OUT && sideMenuLink?.includes('http')) {
      return {
        ...duccRouteItem,
        path: sideMenuLink,
        target: '_blank',
      };
    } else {
      return {
        ...duccRouteItem,
        getLink() {
          return duccRouteItem.sideMenuLink || duccRouteItem.path;
        },
      };
    }
  }

  const filterDuccRoutesInParentId = (parentId, item) => item.sideMenuParentId === parentId;

  return pushDuccConfigsIn(
    fullRoutes,
    parentId,
    duccRoutes,
    filterDuccRoutesInParentId,
    duccRouteItemMapToSideMenuItem,
    _sortMenus
  );
}

/**
 * 根据duccroutes补充fullRoutes
 *
 * 例子1：
 * fullRoutes: [{menuId: 1}], parentId: 0, duccRoutes: [{parentId: 0, menuId: 2}]
 * return: [{menuId: 1}, {menuId: 2, parentId: 0}]
 *
 * 例子2：
 * fullRoutes: [{menuId: 1}], parentId: 0, duccRoutes: [{parentId: 2, menuId: 2}]
 * return: [{menuId: 1}]
 */
function pushDuccConfigsIn(
  fullRoutes = [],
  parentId,
  duccRoutes = [],
  filterDuccRoutesInParentId,
  mapDuccConfigTo,
  combineSidemenus = (item) => item
) {
  if (!duccRoutes || !duccRoutes.length) return fullRoutes;

  const curDuccRoutes = duccRoutes
    .filter((item) => filterDuccRoutesInParentId(parentId, item, fullRoutes))
    .map(mapDuccConfigTo);

  const _fullRoutes = fullRoutes.map((routeItem) => {
    if (!routeItem.menus) return routeItem;
    return {
      ...routeItem,
      menus: pushDuccConfigsIn(
        routeItem.menus,
        routeItem.menuCode || routeItem.menuId,
        duccRoutes,
        filterDuccRoutesInParentId,
        mapDuccConfigTo,
        combineSidemenus
      ),
    };
  });

  return combineSidemenus([..._fullRoutes, ...curDuccRoutes]);
}

/**
 * 排序menu列表，列表项中的index是绝对位置
 * [{index: 1}, {index: 0}, {a: 3}] -> [{index: 0}, {index: 1}, {a: 3}]
 * [{a: 1}, {index: 4}, {a: 3}] -> [{a: 1}, {a: 3}, {index: 4}]
 */
function _sortMenus(menus) {
  const menusNeedSrot = [],
    menusIsNormal = [];
  menus.forEach((item) => {
    if (item.sideMenuInIndex != null && item.sideMenuInIndex < menus.length) {
      menusNeedSrot.push(item);
    } else {
      menusIsNormal.push(item);
    }
  });
  if (!menusNeedSrot.length) return menusIsNormal;

  const res = Array(menus.length).fill(null);
  menusNeedSrot.forEach((item) => {
    res[item.sideMenuInIndex] = item;
  });

  return res.map((item) => {
    if (item) return item;
    return menusIsNormal.shift();
  }).filter(item => !!item);
}

export { pushDuccRoutesIn, pushSideMenusIn };
